import { Constants } from "@eagerdog/constants";
import { IEventRegisterRow } from "../../../services/helper.service.js";
import { IShow } from "@eagerdog/interfaces";

export namespace ISWSConformation {
  export const getForm = (show?: IShow, isJunior?: boolean): IEventRegisterRow[] => {
    // Helper to check if a level is available in show elements
    const isLevelAvailable = (level: string, parentElement: string, alternativeParents?: string[]): boolean => {
      if (!show?.show_elements || show.show_elements.length === 0) {
        return true; // Backward compatibility - show all for legacy shows
      }
      
      // Check all possible parent element names (support both old and new naming)
      const possibleParents = [parentElement, ...(alternativeParents || [])];
      
      // Find ALL matching parent elements
      const matchingParents = show.show_elements.filter(se => possibleParents.includes(se.show_element));
      
      if (matchingParents.length === 0) {
        return false; // Parent section not selected, hide this level
      }
      
      // Check if ANY matching parent has this level OR has no level specified (show all)
      return matchingParents.some(parentSe => {
        // If parent exists but has no level specified (or empty level), show ALL levels in that section
        if (!parentSe.level || parentSe.level === '' || parentSe.level === 'None') {
          return true;
        }
        // If parent has a specific level, check if it matches
        return parentSe.level === level;
      });
    };

    // Filter options for each section
    // Support both old format (conformation_*) and new format (Conformation, Altered Conformation, etc.)
    const regularOptions = [
      // { value: Constants.dog_class_element_level.isws_puppy_3_6, id: Constants.dog_class_element_level.isws_puppy_3_6 }, // Moved to non-regular
      { value: Constants.dog_class_element_level.isws_puppy_6_12, id: Constants.dog_class_element_level.isws_puppy_6_12 },
      { value: Constants.dog_class_element_level.isws_puppy_6_9, id: Constants.dog_class_element_level.isws_puppy_6_9 },
      { value: Constants.dog_class_element_level.isws_puppy_9_12, id: Constants.dog_class_element_level.isws_puppy_9_12 },
      { value: Constants.dog_class_element_level.isws_junior_puppy, id: Constants.dog_class_element_level.isws_junior_puppy },
      { value: Constants.dog_class_element_level.isws_senior_puppy, id: Constants.dog_class_element_level.isws_senior_puppy },
      { value: Constants.dog_class_element_level.isws_1218, id: Constants.dog_class_element_level.isws_1218 },
      { value: Constants.dog_class_element_level.isws_novice, id: Constants.dog_class_element_level.isws_novice },
      { value: Constants.dog_class_element_level.isws_canadian_bred, id: Constants.dog_class_element_level.isws_canadian_bred },
      { value: Constants.dog_class_element_level.isws_american_bred, id: Constants.dog_class_element_level.isws_american_bred },
      { value: Constants.dog_class_element_level.isws_bred_by_exibitor, id: Constants.dog_class_element_level.isws_bred_by_exibitor },
      { value: Constants.dog_class_element_level.isws_open, id: Constants.dog_class_element_level.isws_open },
      { value: Constants.dog_class_element_level.isws_best_of_breed, id: Constants.dog_class_element_level.isws_best_of_breed },
      { value: Constants.dog_class_element_level.isws_field_performance_champion, id: Constants.dog_class_element_level.isws_field_performance_champion },
      // { value: Constants.dog_class_element_level.isws_performance_champion, id: Constants.dog_class_element_level.isws_performance_champion }, // Hidden for now
      // { value: Constants.dog_class_element_level.isws_champion, id: Constants.dog_class_element_level.isws_champion }, // Hidden for now
    ].filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation, ["Conformation"]));

    const alteredOptions = [
      { value: Constants.dog_class_element_level.isws_altered_novice, id: Constants.dog_class_element_level.isws_altered_novice },
      { value: Constants.dog_class_element_level.isws_altered_open, id: Constants.dog_class_element_level.isws_altered_open },
      { value: Constants.dog_class_element_level.isws_altered_champion, id: Constants.dog_class_element_level.isws_altered_champion },
    ].filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_altered, ["Altered Conformation"]));

    const nonRegularOptions = [
      { value: Constants.dog_class_element_level.isws_puppy_3_6, id: Constants.dog_class_element_level.isws_puppy_3_6 }, // Moved from regular classes
      { value: Constants.dog_class_element_level.isws_veteran, id: Constants.dog_class_element_level.isws_veteran },
      { value: Constants.dog_class_element_level.isws_veteran_7_10, id: Constants.dog_class_element_level.isws_veteran_7_10 },
      { value: Constants.dog_class_element_level.isws_veteran_10_plus, id: Constants.dog_class_element_level.isws_veteran_10_plus },
      { value: Constants.dog_class_element_level.isws_veteran_10_13, id: Constants.dog_class_element_level.isws_veteran_10_13 },
      { value: Constants.dog_class_element_level.isws_veteran_13_plus, id: Constants.dog_class_element_level.isws_veteran_13_plus },
    ].filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_non_regular, ["Non-Regular Classes"]));

    const sweepstakesOptions = [
      // Puppy Sweepstakes
      { value: Constants.dog_class_element_level.isws_sweeps_puppy_3_6, id: Constants.dog_class_element_level.isws_sweeps_puppy_3_6 },
      { value: Constants.dog_class_element_level.isws_sweeps_puppy_6_12, id: Constants.dog_class_element_level.isws_sweeps_puppy_6_12 },
      { value: Constants.dog_class_element_level.isws_sweeps_12_24, id: Constants.dog_class_element_level.isws_sweeps_12_24 },
      // Veterans Sweepstakes
      { value: Constants.dog_class_element_level.isws_sweeps_veteran_7_plus, id: Constants.dog_class_element_level.isws_sweeps_veteran_7_plus },
      { value: Constants.dog_class_element_level.isws_sweeps_veteran_7_10, id: Constants.dog_class_element_level.isws_sweeps_veteran_7_10 },
      { value: Constants.dog_class_element_level.isws_sweeps_veteran_10_plus, id: Constants.dog_class_element_level.isws_sweeps_veteran_10_plus },
      { value: Constants.dog_class_element_level.isws_sweeps_veteran_7_9, id: Constants.dog_class_element_level.isws_sweeps_veteran_7_9 },
      { value: Constants.dog_class_element_level.isws_sweeps_veteran_9_13, id: Constants.dog_class_element_level.isws_sweeps_veteran_9_13 },
      { value: Constants.dog_class_element_level.isws_sweeps_veteran_13_plus, id: Constants.dog_class_element_level.isws_sweeps_veteran_13_plus },
    ].filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_sweepstakes, ["Sweepstakes"]));

    const juniorOptions = [
      { value: Constants.dog_class_element_level.isws_junior_handler_1, id: Constants.dog_class_element_level.isws_junior_handler_1 },
      { value: Constants.dog_class_element_level.isws_junior_handler_2, id: Constants.dog_class_element_level.isws_junior_handler_2 },
      { value: Constants.dog_class_element_level.isws_junior_showmanship_1, id: Constants.dog_class_element_level.isws_junior_showmanship_1 },
      { value: Constants.dog_class_element_level.isws_junior_showmanship_2, id: Constants.dog_class_element_level.isws_junior_showmanship_2 },
    ].filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_junior, ["Junior Handler/Showmanship"]));

    const specialOptions = [
      { value: Constants.dog_class_element_level.isws_brace, id: Constants.dog_class_element_level.isws_brace },
      { value: Constants.dog_class_element_level.isws_stud_dog, id: Constants.dog_class_element_level.isws_stud_dog },
      { value: Constants.dog_class_element_level.isws_brood_bitch, id: Constants.dog_class_element_level.isws_brood_bitch },
    ].filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_special_classes, ["Special Classes"]));

    // Build form rows dynamically
    const formRows: IEventRegisterRow[] = [];
    
    // Row 1: Regular + Altered dropdowns (if any options available)
    if (regularOptions.length > 0 || alteredOptions.length > 0) {
      const row: IEventRegisterRow = {
        allowDuplicates: false,
        inputs: []
      };
      
      if (regularOptions.length > 0) {
        row.inputs.push({
          type: "dropdown",
          flex: 2,
          id: Constants.dog_class_element.conformation,
          label: Constants.dog_class_element.conformation,
          options: [{ value: "None", id: "None" }, ...regularOptions]
        });
      }
      
      if (alteredOptions.length > 0) {
        row.inputs.push({
          type: "dropdown",
          flex: 2,
          id: Constants.dog_class_element.conformation_altered,
          label: Constants.dog_class_element.conformation_altered,
          options: [{ value: "None", id: "None" }, ...alteredOptions]
        });
      }
      
      formRows.push(row);
    }
    
    // Row 2: Non-Regular Classes (checkboxes)
    if (nonRegularOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "checkbox",
          label: Constants.dog_class_element.conformation_non_regular,
          multiSelect: true,
          options: nonRegularOptions
        }]
      });
    }
    
    // Row 3: Sweepstakes Classes (checkboxes)
    if (sweepstakesOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "checkbox",
          label: Constants.dog_class_element.conformation_sweepstakes,
          multiSelect: true,
          options: sweepstakesOptions
        }]
      });
    }
    
    // Row 4: Junior Handler/Showmanship (checkboxes) - ONLY show if user is junior
    if (isJunior && juniorOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "checkbox",
          label: Constants.dog_class_element.conformation_junior,
          multiSelect: true,
          options: juniorOptions
        }]
      });
    }
    
    // Row 5: Special Classes (checkboxes)
    if (specialOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "checkbox",
          label: Constants.dog_class_element.conformation_special_classes,
          multiSelect: true,
          options: specialOptions
        }]
      });
    }
    
    return formRows;
  };

  // Backward compatibility
  export const form: IEventRegisterRow[] = getForm();
}
